import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from './store/authStore';
import Header from './components/Header';
import AuthForm from './components/AuthForm';
import CategoryCard from './components/CategoryCard';
import { LegalCategory } from './types';

function App() {
  const { t } = useTranslation();
  const { loading } = useAuthStore();
  const [showAuthForm, setShowAuthForm] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const categories: Array<{
    id: LegalCategory;
    title: string;
    description: string;
  }> = [
    {
      id: 'tenant-landlord',
      title: t('categories.tenant-landlord.title'),
      description: t('categories.tenant-landlord.description'),
    },
    {
      id: 'contract-review',
      title: t('categories.contract-review.title'),
      description: t('categories.contract-review.description'),
    },
    {
      id: 'traffic-tickets',
      title: t('categories.traffic-tickets.title'),
      description: t('categories.traffic-tickets.description'),
    },
    {
      id: 'employment',
      title: t('categories.employment.title'),
      description: t('categories.employment.description'),
    },
    {
      id: 'family-law',
      title: t('categories.family-law.title'),
      description: t('categories.family-law.description'),
    },
    {
      id: 'consumer-protection',
      title: t('categories.consumer-protection.title'),
      description: t('categories.consumer-protection.description'),
    },
    {
      id: 'estate-planning',
      title: t('categories.estate-planning.title'),
      description: t('categories.estate-planning.description'),
    },
  ];

  const handleCategoryClick = (category: LegalCategory) => {
    console.log('Selected category:', category);
    // TODO: Implement category selection logic
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onAuthClick={() => setShowAuthForm(true)} />
      
      {showAuthForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <AuthForm onClose={() => setShowAuthForm(false)} />
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('main.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('main.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              category={category.id}
              title={category.title}
              description={category.description}
              onClick={() => handleCategoryClick(category.id)}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <img
            src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
            alt={t('main.imageAlt')}
            className="rounded-lg shadow-xl mx-auto max-w-4xl"
          />
        </div>
      </main>

      <footer className="bg-gray-800 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.about.title')}</h3>
              <p className="text-gray-300">
                {t('footer.about.description')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.legal.title')}</h3>
              <p className="text-gray-300">
                {t('footer.legal.description')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.contact.title')}</h3>
              <p className="text-gray-300">
                {t('footer.contact.email')}<br />
                {t('footer.contact.availability')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;